import { useEffect, useRef } from 'react';

/**
 * "Deleted “Title”. Undo" — shown for a few seconds after a delete.
 *
 * Delete stays one click with no confirm, because it can be undone: the material sits in the
 * trash for thirty days, and this is the quickest way back. After the notice goes, the
 * "Recently deleted" section at the foot of the room still has it.
 *
 * `role="status"` with a polite live region, so a screen reader hears that the delete
 * happened without being interrupted. The Undo button sits inside it and is reachable by Tab.
 */

export const UNDO_NOTICE_MS = 8000;

export interface UndoNoticeProps {
  title: string;
  onUndo: () => void;
  /** Called when the time runs out. The caller drops the notice. */
  onExpire: () => void;
  busy?: boolean;
  timeoutMs?: number;
}

export default function UndoNotice({
  title, onUndo, onExpire, busy = false, timeoutMs = UNDO_NOTICE_MS,
}: UndoNoticeProps) {
  // The latest callback, so a re-render does not restart the clock.
  const expire = useRef(onExpire);
  useEffect(() => { expire.current = onExpire; }, [onExpire]);

  useEffect(() => {
    const timer = setTimeout(() => expire.current(), timeoutMs);
    return () => clearTimeout(timer);
  }, [timeoutMs]);

  return (
    <div className="undo-notice" role="status" aria-live="polite">
      <span>Deleted &ldquo;{title}&rdquo;.</span>
      <button type="button" className="link" onClick={onUndo} disabled={busy}>
        Undo
      </button>
    </div>
  );
}
